import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import { submitAISmsTemplateFilterData } from "model/AISmsTemplateModel";
import { useAISmsTemplateContext } from "libs/context/AISmsTemplateContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const AISmsTemplate = () => {
    const { permissionData } = usePermissionContext() || {};
    const navigate = useNavigate();
    const {
        fetchAISmsTemplateContextData,
        aiSmsTemplateContextData,
    } = useAISmsTemplateContext() || {};

    const [searchQuery, setSearchQuery] = useState("");
    const [aiSmsTemplateData, setAISmsTemplateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return aiSmsTemplateData;

        return aiSmsTemplateData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [aiSmsTemplateData]);

    const aiSmsTemplateActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.AISmsTemplateAddAccess),
            canEdit: Boolean(permissionData?.AISmsTemplateUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyAISmsTemplateRowAction = useMemo(() => {
        const { canCreate, ...rowActions } = aiSmsTemplateActionPermissions;
        return Object.values(rowActions).some(Boolean);
    }, [aiSmsTemplateActionPermissions]);

    useEffect(() => {
        fetchAISmsTemplateContextData();
        getAISmsTemplateList();
    }, []);

    useEffect(() => {
        const valueReModified = aiSmsTemplateContextData?.table?.columns?.map((col) => {
            const field = col?.field;
            const normalizedField = String(field ?? "").trim().toLowerCase();
            const isStatusColumn =
                normalizedField === "status" ||
                normalizedField === "statusid" ||
                String(col?.type ?? "").trim().toLowerCase() === "status";

            return {
                Header: col?.title,
                accessor: isStatusColumn ? "statusId" : field,
                type: col?.type,
            };
        });
        setTableMetadata(valueReModified);
    }, [aiSmsTemplateContextData]);

    const getAISmsTemplateList = async (payload = {}) => {
        setLoading(true);
        const response = await submitAISmsTemplateFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];

        if (response) {
            setAISmsTemplateData(Array.isArray(listData) ? listData : []);
        } else {
            setAISmsTemplateData([]);
        }
        setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
        await getAISmsTemplateList(data);
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        await getAISmsTemplateList({ nameContains: value?.trim() || "" });
    };

    const handleEdit = (row) => {
        const templateId = row?.id ?? row?.Id;
        if (!templateId) return;
        const templateName = row?.name ?? row?.Name ?? row?.templateName ?? "";
        navigate(`/${urlEndPoint.addEditAISms}/${templateId}`, {
            state: { templateName },
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">AI SMS Template</h5>
                                {aiSmsTemplateActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() =>
                                                navigate(`/${urlEndPoint.addEditAISms}`)
                                            }
                                        >
                                            + New Template
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyAISmsTemplateRowAction}
                                    isEditAccess={aiSmsTemplateActionPermissions.canEdit}
                                    onEdit={
                                        aiSmsTemplateActionPermissions.canEdit
                                            ? handleEdit
                                            : undefined
                                    }
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"AISmsTemplate"}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="AI SMS Template"
                        handleSubmit={handleFilterSubmit}
                        filterData={aiSmsTemplateContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

AISmsTemplate.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(AISmsTemplate);
